/**
 * Nature d'un appareil (`trackers.kind`, V13). `BAROMETER` = ancre barométrique DPS310 posée à
 * demeure (plusieurs par site), positionnée via `PUT /trackers/{id}/barometer-position`.
 * Lecture seule depuis ADR-020 : c'est le préfixe de topic `tk/baro/` qui rend l'appareil baromètre.
 */
export type TrackerKind = 'TRACKER' | 'BAROMETER';

/** Grandeur mesurée par un baromètre (contrat-baro v2) : pression (hPa), température (°C), batterie (%). */
export type BaroQuantity = 'baro' | 'temp' | 'bat';

const BARO_QUANTITIES: readonly BaroQuantity[] = ['baro', 'temp', 'bat'];

/**
 * Une mesure live, relayée par websocket-broker sur `/topic/tracker/{trackerId}/baro`
 * (Redis `tracker:baro`, ADR-020) — une grandeur par message.
 */
export interface BaroMeasure {
  trackerId?: string;
  quantity: BaroQuantity;
  value: number;
  /** ISO-8601, heure de réception serveur. */
  timestamp: string;
}

/** Dernière valeur connue d'une grandeur. */
export interface BaroValue {
  value: number;
  /** ISO-8601, heure de réception serveur. */
  timestamp: string;
}

/**
 * `Tracker.lastBaro` (api-tracker 6.0.0) : dernière mesure de chaque grandeur, horodatages
 * indépendants ; une entrée absente = jamais reçue.
 */
export interface LastBaro {
  pressure?: BaroValue;
  temperature?: BaroValue;
  battery?: BaroValue;
}

const FIELD_BY_QUANTITY: Record<BaroQuantity, keyof LastBaro> = {
  baro: 'pressure',
  temp: 'temperature',
  bat: 'battery',
};

/** Parse un message STOMP `/baro` (objet déjà parsé ou JSON brut) ; null si illisible, sans valeur numérique ou grandeur inconnue. */
export function parseBaroMeasure(msg: unknown): BaroMeasure | null {
  let obj: unknown;
  try {
    obj = typeof msg === 'string' ? JSON.parse(msg) : msg;
  } catch {
    return null;
  }
  if (!obj || typeof obj !== 'object') return null;
  const o = obj as Record<string, unknown>;
  const quantity = o['quantity'];
  const value = o['value'];
  const timestamp = o['timestamp'];
  if (typeof quantity !== 'string' || !BARO_QUANTITIES.includes(quantity as BaroQuantity)) return null;
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  if (typeof timestamp !== 'string') return null;
  return {
    ...(typeof o['trackerId'] === 'string' ? { trackerId: o['trackerId'] } : {}),
    quantity: quantity as BaroQuantity,
    value,
    timestamp,
  };
}

/**
 * Fusionne une mesure dans la dernière mesure connue, grandeur par grandeur. Une entrée n'est
 * remplacée que si la mesure est au moins aussi récente (un message en retard n'écrase rien).
 * Pur : renvoie un nouvel objet, ou `last` inchangé si la mesure est ignorée.
 */
export function mergeBaroMeasure(last: LastBaro | null | undefined, m: BaroMeasure): LastBaro {
  const base: LastBaro = last ?? {};
  const field = FIELD_BY_QUANTITY[m.quantity];
  const current = base[field];
  if (current && Date.parse(m.timestamp) < Date.parse(current.timestamp)) return base;
  return { ...base, [field]: { value: m.value, timestamp: m.timestamp } };
}

export function isBarometer(t: { kind?: string | null } | null | undefined): boolean {
  return t?.kind === 'BAROMETER';
}
